import { Component, type ErrorInfo, type ReactNode } from 'react';

import { lookupBlock, type BlockContext } from './registry';
import { reportCms } from './diagnostics';

/**
 * The recursive renderer.
 *
 * ── IT RESOLVES, IT DOES NOT CALCULATE ───────────────────────────────────────────────
 *
 * Look up the component, check it belongs in this context, check the version, hand it its
 * props and its rendered children. No business logic, no data fetching, no conditional
 * product behaviour. Everything a block needs comes from the context the host published.
 *
 * ── IT REFUSES RATHER THAN IMPROVISES ────────────────────────────────────────────────
 *
 * The server validates documents before storing them, so a bad node should be impossible
 * here. This checks anyway, because "impossible" assumes the server that validated it is
 * the server that served it — and a registry that has moved on since a document was
 * published makes yesterday's valid document today's unknown component.
 */

export interface BlockNode {
  id: string;
  component: string;
  v: number;
  props?: Record<string, unknown>;
  children?: BlockNode[];
}

export interface BlockDocument {
  schemaVersion: number;
  root: BlockNode;
}

export const SUPPORTED_SCHEMA_VERSION = 1;

/** Reported upward so the host can decide to fall back rather than ship a broken page. */
export type RenderProblem =
  | { kind: 'unsupported_schema'; got: number; expected: number }
  | { kind: 'unknown_component'; nodeId: string; component: string }
  | { kind: 'wrong_context'; nodeId: string; component: string; context: string }
  | { kind: 'version_mismatch'; nodeId: string; component: string; got: number; expected: number }
  | { kind: 'threw'; nodeId: string; component: string; message: string };

/** Keys a document may never carry into a props object. */
const FORBIDDEN_PROPS = new Set(['__proto__', 'constructor', 'prototype', 'key', 'ref',
  'dangerouslySetInnerHTML']);

/**
 * Props, stripped to a null-prototype object.
 *
 * `dangerouslySetInnerHTML` is refused here as well as being absent from every schema:
 * the day somebody adds an HTML block, the renderer should already be the layer that
 * says no. `key` and `ref` are refused because they are React's, not a document's.
 */
function safeProps(props: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = Object.create(null);
  if (!props || typeof props !== 'object') return out;
  for (const key of Object.keys(props)) {
    if (FORBIDDEN_PROPS.has(key)) continue;
    out[key] = props[key];
  }
  return out;
}

/**
 * One block's blast radius.
 *
 * A non-critical block that throws renders nothing and the rest of the page survives — a
 * broken lineup should not cost somebody a ticket sale. A CRITICAL block reports upward
 * instead, and the host falls back to the page it has always rendered, because an event
 * page missing its ticket panel is worse than one that took a different route to render.
 */
class BlockBoundary extends Component<
  { nodeId: string; component: string; critical: boolean;
    onProblem: (p: RenderProblem) => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    // Reported with enough context to find it — component, node, criticality — and with
    // nothing about the customer or their order.
    reportCms(
      this.props.critical ? 'cms_critical_block_failed' : 'cms_optional_block_failed',
      {
        component: this.props.component,
        nodeId: this.props.nodeId,
        critical: this.props.critical,
        message: error.message,
      },
    );
    this.props.onProblem({
      kind: 'threw',
      nodeId: this.props.nodeId,
      component: this.props.component,
      message: error.message,
    });
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export function BlockRenderer({
  node, context, onProblem,
}: {
  node: BlockNode;
  context: BlockContext;
  onProblem: (problem: RenderProblem) => void;
}) {
  const entry = lookupBlock(node?.component);

  if (!entry) {
    // A document naming a component this build does not have. The public answer is to
    // render nothing and tell the host; an editor preview can show a placeholder, which
    // is a task 12 concern.
    reportCms('cms_document_invalid_at_runtime', {
      reason: 'unknown_component', nodeId: node?.id ?? '?', component: String(node?.component),
    });
    onProblem({ kind: 'unknown_component', nodeId: node?.id ?? '?', component: String(node?.component) });
    return null;
  }

  if (!entry.contexts.includes(context)) {
    reportCms('cms_document_invalid_at_runtime', {
      reason: 'wrong_context', nodeId: node.id, component: node.component,
    });
    onProblem({ kind: 'wrong_context', nodeId: node.id, component: node.component, context });
    return null;
  }

  // ── VERSION MISMATCH: REFUSE, NEVER REINTERPRET ────────────────────────────
  //
  // A document pinned to EventHero v1 against a registry holding v2 is not rendered with
  // v2's component. The two versions exist precisely because their props mean different
  // things, and guessing would silently render a page nobody designed. The block is
  // skipped and reported; migrating a document to a new block version is an explicit
  // step, not something the renderer does on somebody's behalf.
  if (node.v !== entry.version) {
    reportCms('cms_document_invalid_at_runtime', {
      reason: 'version_mismatch', nodeId: node.id, component: node.component,
      componentVersion: node.v, expectedVersion: entry.version,
    });
    onProblem({
      kind: 'version_mismatch', nodeId: node.id, component: node.component,
      got: node.v, expected: entry.version,
    });
    return null;
  }

  const children = (node.children ?? []).map((child) => (
    <BlockRenderer key={child.id} node={child} context={context} onProblem={onProblem} />
  ));

  const { Live } = entry;
  const props = safeProps(node.props);

  return (
    <BlockBoundary
      nodeId={node.id}
      component={node.component}
      critical={Boolean(entry.critical)}
      onProblem={onProblem}
    >
      {entry.children === 'none'
        ? <Live {...props} />
        : <Live {...props}>{children}</Live>}
    </BlockBoundary>
  );
}

/**
 * Render a whole document.
 *
 * Returns null for a document this build cannot render at all, which the host treats as
 * "use the page you have always rendered".
 */
export function DocumentRenderer({
  document, context, onProblem,
}: {
  document: BlockDocument;
  context: BlockContext;
  onProblem: (problem: RenderProblem) => void;
}) {
  // REPORTED, not just refused. Returning null silently would render an EMPTY page: the
  // host would never learn the document was unusable and would never fall back. The first
  // version of this function did exactly that, and the functional regression caught it.
  if (!document || document.schemaVersion !== SUPPORTED_SCHEMA_VERSION || !document.root) {
    reportCms('cms_document_invalid_at_runtime', {
      reason: 'unsupported_schema_version',
      componentVersion: document?.schemaVersion ?? -1,
      expectedVersion: SUPPORTED_SCHEMA_VERSION,
    });
    onProblem({
      kind: 'unsupported_schema',
      got: document?.schemaVersion ?? -1,
      expected: SUPPORTED_SCHEMA_VERSION,
    });
    return null;
  }
  return <BlockRenderer node={document.root} context={context} onProblem={onProblem} />;
}

/** Does this document reference a critical block? Used to decide fallback severity. */
export function documentUsesCriticalBlock(document: BlockDocument): boolean {
  const walk = (node: BlockNode | undefined): boolean => {
    if (!node) return false;
    if (lookupBlock(node.component)?.critical) return true;
    return (node.children ?? []).some(walk);
  };
  return walk(document?.root);
}
